// ============================================================================
// Login.jsx — Repeaked
// Auth logic: loginUser, login({email, password}) from original Login.js.
// Layout: height:100vh, no scroll, footer always visible.
// ============================================================================

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getStoredBetaAccessToken, login, steamLogin } from '../api/api';
import { useAuth } from '../context/AuthContext';

// ---------------------------------------------------------------------------
// Brand tokens
// ---------------------------------------------------------------------------
const C = {
  red:         '#E8192C',
  redDim:      '#a4111d',
  redGlow:     'rgba(232, 25, 44, 0.45)',
  orange:      '#FF6B2B',
  bg:          '#0C0905',
  bgWarm:      '#100B07',
  text:        '#F0F2F5',
  textDim:     '#9A938B',
  textVeryDim: '#5C554E',
  card:        '#111111',
  cardAlt:     '#16110D',
  border:      '#1e1e1e',
  borderBright:'#2A2520',
  greenLive:   '#3FCB7E',
  steam:       '#66c0f4',
};

const F = {
  display: '"Bebas Neue", "Oswald", Impact, sans-serif',
  body:    '"Rajdhani", "Inter", system-ui, -apple-system, sans-serif',
};

const DISCORD_URL = 'https://discord.gg/CcpV64MAPW';

// ---------------------------------------------------------------------------
// Error normaliser
// ---------------------------------------------------------------------------
const normaliseError = (detail) => {
  if (!detail) return 'Login failed';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || JSON.stringify(e)).join(', ');
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
};

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
const useIsMobile = (bp = 960) => {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const h = () => setM(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return m;
};

const useHover = () => {
  const [h, setH] = useState(false);
  return [h, { onMouseEnter: () => setH(true), onMouseLeave: () => setH(false) }];
};

// ---------------------------------------------------------------------------
// Atoms
// ---------------------------------------------------------------------------
const Diamond = ({ size = 16, color = C.red, dotColor = C.orange, withDot = false, hollow = true, style }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block', width: size, height: size,
      border: `${Math.max(1.5, size / 9)}px solid ${color}`,
      background: hollow ? 'transparent' : color,
      transform: 'rotate(45deg)', position: 'relative',
      boxSizing: 'border-box', flexShrink: 0, ...style,
    }}
  >
    {withDot && (
      <span style={{
        position: 'absolute', top: '50%', left: '50%',
        width: size * 0.32, height: size * 0.32, background: dotColor,
        transform: 'translate(-50%, -50%)',
      }} />
    )}
  </span>
);

const LogoLockup = ({ size = 22 }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: size * 0.6 }}>
    <Diamond size={size} withDot />
    <span style={{ fontFamily: F.display, fontSize: size * 1.1, letterSpacing: 2, lineHeight: 1, color: C.text }}>
      RE<span style={{ color: C.red }}>PEAKED</span>
    </span>
  </div>
);

const LogoButton = ({ onClick }) => {
  const [hover, hp] = useHover();
  return (
    <button onClick={onClick} aria-label="Repeaked home" {...hp} style={{
      background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
      display: 'flex', alignItems: 'center',
      opacity: hover ? 1 : 0.95,
      transform: hover ? 'translateY(-1px)' : 'translateY(0)',
      transition: 'opacity 160ms ease, transform 160ms ease',
    }}>
      <LogoLockup size={20} />
    </button>
  );
};

const Eyebrow = ({ children }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 10,
    fontFamily: F.body, fontSize: 11, fontWeight: 600,
    letterSpacing: 3, textTransform: 'uppercase', color: C.textDim,
  }}>
    <Diamond size={7} color={C.red} hollow={false} />
    <span>{children}</span>
  </div>
);

const StatusDot = ({ label }) => {
  const [on, setOn] = useState(true);
  useEffect(() => { const i = setInterval(() => setOn(o => !o), 900); return () => clearInterval(i); }, []);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{
        width: 10, height: 10, borderRadius: '50%', background: C.greenLive, flexShrink: 0,
        boxShadow: on ? `0 0 0 0 ${C.greenLive}55, 0 0 12px ${C.greenLive}` : `0 0 0 6px ${C.greenLive}00, 0 0 4px ${C.greenLive}66`,
        transition: 'box-shadow 900ms ease-out',
      }} />
      <span style={{ fontFamily: F.body, fontSize: 11, letterSpacing: 2.5, textTransform: 'uppercase', color: C.text, fontWeight: 600 }}>
        {label}
      </span>
    </div>
  );
};

// ---------------------------------------------------------------------------
// DriftField
// ---------------------------------------------------------------------------
const DriftField = () => {
  const [diamonds, setDiamonds] = useState(() =>
    Array.from({ length: 14 }, (_, i) => ({
      id: i,
      x: Math.random() * 100, y: Math.random() * 100,
      size: 24 + Math.random() * 60, rot: Math.random() * 360,
      vx: (Math.random() - 0.5) * 0.04, vy: (Math.random() - 0.5) * 0.03 - 0.005,
      vrot: (Math.random() - 0.5) * 0.15,
      opacity: 0.04 + Math.random() * 0.07,
      color: Math.random() > 0.8 ? C.orange : C.red,
    }))
  );
  useEffect(() => {
    let raf; let lastTick = performance.now();
    const tick = (now) => {
      const dt = Math.min(50, now - lastTick); lastTick = now;
      const step = dt / 16.67;
      setDiamonds(prev => prev.map(d => {
        let nx = d.x + d.vx * step; let ny = d.y + d.vy * step;
        if (nx > 110) nx = -10; if (nx < -10) nx = 110;
        if (ny > 110) ny = -10; if (ny < -10) ny = 110;
        return { ...d, x: nx, y: ny, rot: d.rot + d.vrot * step };
      }));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div aria-hidden="true" style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 1 }}>
      {diamonds.map(d => (
        <span key={d.id} style={{
          position: 'absolute', left: `${d.x}%`, top: `${d.y}%`,
          width: d.size, height: d.size, border: `1.5px solid ${d.color}`,
          opacity: d.opacity, transform: `translate(-50%, -50%) rotate(${d.rot}deg)`,
          boxSizing: 'border-box',
        }} />
      ))}
    </div>
  );
};

const Grain = () => (
  <div aria-hidden="true" style={{
    position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 9999,
    opacity: 0.05, mixBlendMode: 'overlay',
    backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22160%22 height=%22160%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.9%22 numOctaves=%222%22 stitchTiles=%22stitch%22/><feColorMatrix values=%220 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.5 0%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>")',
    backgroundSize: '160px 160px',
  }} />
);

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------
const PrimaryButton = ({ children, type = 'button', disabled, onClick }) => {
  const [hover, hp] = useHover();
  const [active, setActive] = useState(false);
  return (
    <button
      type={type} disabled={disabled} onClick={onClick}
      onMouseDown={() => setActive(true)} onMouseUp={() => setActive(false)}
      {...hp}
      style={{
        fontFamily: F.display, fontSize: 15, letterSpacing: 2.5,
        padding: '12px 28px', background: disabled ? C.redDim : C.red,
        color: C.text, border: 'none', borderRadius: 2,
        cursor: disabled ? 'not-allowed' : 'pointer', textTransform: 'uppercase',
        transform: active && !disabled ? 'translateY(1px)' : hover && !disabled ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover && !disabled ? `0 8px 20px -8px ${C.redGlow}, 0 0 0 1px ${C.orange}33` : `0 4px 12px -6px ${C.redGlow}`,
        transition: 'transform 160ms ease, box-shadow 200ms ease, background 160ms ease',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: 10, whiteSpace: 'nowrap', opacity: disabled ? 0.7 : 1, width: '100%',
      }}
    >
      {children}
    </button>
  );
};

const SteamButton = ({ onClick }) => {
  const [hover, hp] = useHover();
  const [active, setActive] = useState(false);
  return (
    <button
      type="button" onClick={onClick}
      onMouseDown={() => setActive(true)} onMouseUp={() => setActive(false)}
      {...hp}
      style={{
        fontFamily: F.display, fontSize: 15, letterSpacing: 2.5,
        padding: '12px 28px',
        background: hover ? '#0d1c2c' : 'transparent',
        color: C.text, border: `1.5px solid ${hover ? C.steam : C.borderBright}`,
        borderRadius: 2, cursor: 'pointer', textTransform: 'uppercase',
        transform: active ? 'translateY(1px)' : hover ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hover ? `0 8px 20px -8px rgba(102,192,244,0.35)` : 'none',
        transition: 'transform 160ms ease, background 160ms ease, border-color 160ms ease, box-shadow 200ms ease',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: 10, whiteSpace: 'nowrap', width: '100%',
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill={hover ? C.steam : C.text} aria-hidden="true">
        <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.624 0 11.998-5.373 11.998-12.001C23.977 5.373 18.603 0 11.979 0z"/>
      </svg>
      Sign in with Steam
    </button>
  );
};

// ---------------------------------------------------------------------------
// Field
// ---------------------------------------------------------------------------
const Field = ({ label, type = 'text', value, onChange, autoComplete, placeholder, id, rightSlot }) => {
  const [focus, setFocus] = useState(false);
  const [hover, hp] = useHover();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={{
        fontFamily: F.body, fontSize: 10, letterSpacing: 2.5,
        textTransform: 'uppercase', color: C.textDim, fontWeight: 700,
      }}>
        {label}
      </label>
      <div {...hp} style={{
        position: 'relative', display: 'flex', alignItems: 'center',
        background: focus ? C.cardAlt : C.card,
        border: `1px solid ${focus ? C.red : hover ? C.borderBright : C.border}`,
        borderLeft: `2px solid ${focus ? C.red : C.borderBright}`,
        transition: 'background 160ms ease, border-color 160ms ease',
      }}>
        <input
          id={id} type={type} value={value} onChange={onChange}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          autoComplete={autoComplete} placeholder={placeholder}
          style={{
            flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none',
            color: C.text, fontFamily: F.body, fontSize: 15, fontWeight: 500,
            letterSpacing: 0.3, padding: '11px 14px',
            paddingRight: rightSlot ? 4 : 14,
          }}
        />
        {rightSlot}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Right panel stat
// ---------------------------------------------------------------------------
const PanelStat = ({ value, label, accent }) => (
  <div style={{
    padding: '14px 16px',
    background: accent ? `linear-gradient(180deg, ${C.cardAlt}, ${C.card})` : C.card,
    border: `1px solid ${accent ? C.borderBright : C.border}`,
    borderLeft: `2px solid ${accent ? C.red : C.borderBright}`,
    display: 'flex', flexDirection: 'column', gap: 3,
  }}>
    <div style={{ fontFamily: F.display, fontSize: 28, letterSpacing: 1, lineHeight: 1, color: C.text }}>{value}</div>
    <div style={{ fontFamily: F.body, fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: C.textDim, fontWeight: 600 }}>{label}</div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function Login() {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const { loginUser }           = useAuth();
  const navigate                = useNavigate();
  const isMobile                = useIsMobile(960);
  const isSmall                 = useIsMobile(560);

  const [mounted, setMounted] = useState(false);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 60); return () => clearTimeout(t); }, []);

  const fadeUp = (delay = 0) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(14px)',
    transition: `opacity 600ms ease ${delay}ms, transform 600ms cubic-bezier(0.2, 0.7, 0.2, 1) ${delay}ms`,
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await login({ email, password });
      await loginUser(res.data);
      navigate('/dashboard');
    } catch (err) {
      setError(normaliseError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleSteamLogin = () => {
    steamLogin(getStoredBetaAccessToken());
  };

  return (
    <div style={{
      background: C.bg, color: C.text, fontFamily: F.body,
      // Lock to viewport — no scroll, footer always visible
      height: '100vh', overflow: 'hidden',
      position: 'relative',
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Rajdhani:wght@400;500;600;700&display=swap');
        html, body { background: ${C.bg}; margin: 0; padding: 0; height: 100%; overflow: hidden; }
        #root { height: 100%; overflow: hidden; }
        * { box-sizing: border-box; }
        ::selection { background: ${C.red}; color: ${C.text}; }
        a:focus-visible, button:focus-visible { outline: 2px solid ${C.orange}; outline-offset: 3px; }
        input::placeholder { color: ${C.textVeryDim}; }
        input:-webkit-autofill, input:-webkit-autofill:focus {
          -webkit-box-shadow: 0 0 0 1000px ${C.card} inset !important;
          -webkit-text-fill-color: ${C.text} !important;
          caret-color: ${C.text};
          transition: background-color 99999s ease-out 0s;
        }
      `}</style>

      <Grain />
      <DriftField />

      {/* Full-height flex column */}
      <div style={{
        position: 'relative', zIndex: 2,
        height: '100%', display: 'flex', flexDirection: 'column',
      }}>

        {/* NAV — fixed height, never shrinks */}
        <nav style={{
          background: 'rgba(12,9,5,0.4)', backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)', borderBottom: `1px solid ${C.border}`,
          flexShrink: 0,
        }}>
          <div style={{
            maxWidth: 1280, margin: '0 auto',
            padding: isSmall ? '12px 20px' : '14px 32px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
          }}>
            <LogoButton onClick={() => navigate('/')} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              {!isSmall && (
                <span style={{
                  fontFamily: F.body, fontSize: 12, fontWeight: 600,
                  letterSpacing: 2.5, textTransform: 'uppercase', color: C.textDim,
                }}>
                  No account?
                </span>
              )}
              <Link to="/register" style={{
                fontFamily: F.display, fontSize: 13, letterSpacing: 2,
                textTransform: 'uppercase', color: C.text, textDecoration: 'none',
                padding: '7px 14px', border: `1.5px solid ${C.borderBright}`, borderRadius: 2,
              }}>
                Register
              </Link>
            </div>
          </div>
        </nav>

        {/* SPLIT BODY — fills remaining space between nav and footer */}
        <div style={{
          flex: 1, minHeight: 0,
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          maxWidth: 1280, width: '100%', margin: '0 auto',
          padding: isSmall ? '24px 20px' : isMobile ? '32px 32px' : '40px 32px',
          gap: isMobile ? 32 : 64,
          alignItems: 'center',
        }}>

          {/* LEFT: FORM */}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', maxWidth: 480, width: '100%' }}>

            <div style={fadeUp(0)}>
              <Eyebrow>Sign In · CS:GO Legacy</Eyebrow>
            </div>

            <h1 style={{
              ...fadeUp(60),
              fontFamily: F.display,
              fontSize: isSmall ? 52 : isMobile ? 68 : 80,
              letterSpacing: 1.5, lineHeight: 0.9,
              margin: '14px 0 8px 0', color: C.text,
            }}>
              READY<br />UP<span style={{ color: C.red }}>.</span>
            </h1>

            <p style={{
              ...fadeUp(120),
              fontFamily: F.body, fontSize: 15, lineHeight: 1.5,
              color: C.textDim, margin: '0 0 20px 0', fontWeight: 500, maxWidth: 420,
            }}>
              Sign in to your Repeaked account. Queue's been waiting.
            </p>

            {/* Equal-weight Steam + email divider */}
            <div style={{
              ...fadeUp(180),
              display: 'grid',
              gridTemplateColumns: isSmall ? '1fr' : '1fr 1fr',
              gap: 10, marginBottom: 20,
            }}>
              <SteamButton onClick={handleSteamLogin} />
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontFamily: F.body, fontSize: 10, letterSpacing: 3,
                textTransform: 'uppercase', color: C.textVeryDim, fontWeight: 700,
                border: `1.5px dashed ${C.border}`, padding: '12px 16px',
              }}>
                <Diamond size={7} color={C.red} hollow={false} style={{ marginRight: 8 }} />
                Or use email
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleLogin} noValidate style={{
              ...fadeUp(240),
              display: 'flex', flexDirection: 'column', gap: 12,
            }}>
              <Field
                id="login-email" label="Email" type="email"
                value={email} onChange={e => setEmail(e.target.value)}
                autoComplete="email" placeholder="example@gmail.com"
              />
              <Field
                id="login-password" label="Password" type={showPass ? 'text' : 'password'}
                value={password} onChange={e => setPassword(e.target.value)}
                autoComplete="current-password" placeholder="••••••••"
                rightSlot={
                  <button
                    type="button" onClick={() => setShowPass(v => !v)}
                    aria-label={showPass ? 'Hide password' : 'Show password'}
                    style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      fontFamily: F.body, fontSize: 10, fontWeight: 700,
                      letterSpacing: 2, textTransform: 'uppercase',
                      color: C.textVeryDim, padding: '8px 12px',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = C.text)}
                    onMouseLeave={e => (e.currentTarget.style.color = C.textVeryDim)}
                  >
                    {showPass ? 'Hide' : 'Show'}
                  </button>
                }
              />

              {/* Forgot password */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <span style={{
                  fontFamily: F.body, fontSize: 12, fontWeight: 600,
                  letterSpacing: 1.5, color: C.textVeryDim, cursor: 'default',
                }}>
                  Forgot password?
                </span>
              </div>

              {/* Error */}
              {error && (
                <div role="alert" style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  background: 'rgba(232,25,44,0.08)', border: `1px solid rgba(232,25,44,0.25)`,
                  borderLeft: `3px solid ${C.red}`, padding: '10px 14px',
                  color: C.text, fontFamily: F.body, fontSize: 13, fontWeight: 500,
                }}>
                  <Diamond size={8} color={C.red} hollow={false} />
                  {error}
                </div>
              )}

              <PrimaryButton type="submit" disabled={loading}>
                {loading ? 'Signing in…' : 'Sign In →'}
              </PrimaryButton>
            </form>

            {/* Register link */}
            <div style={{
              ...fadeUp(360),
              marginTop: 16, paddingTop: 16,
              borderTop: `1px solid ${C.border}`,
              fontFamily: F.body, fontSize: 13, color: C.textDim, fontWeight: 500,
              display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
            }}>
              <Diamond size={7} color={C.red} hollow={false} />
              <span>New here?</span>
              <Link to="/register" style={{
                color: C.text, fontFamily: F.display, fontSize: 13,
                letterSpacing: 2, textTransform: 'uppercase',
                textDecoration: 'none', borderBottom: `1px solid ${C.red}`, paddingBottom: 1,
              }}>
                Create a free account
              </Link>
            </div>
          </div>

          {/* RIGHT: BRAND PANEL */}
          {!isMobile && (
            <div style={{
              ...fadeUp(200),
              position: 'relative', display: 'flex', flexDirection: 'column',
              justifyContent: 'space-between',
              background: `
                radial-gradient(circle at 80% 20%, rgba(232,25,44,0.10), transparent 55%),
                radial-gradient(circle at 20% 90%, rgba(255,107,43,0.06), transparent 60%),
                ${C.bgWarm}
              `,
              border: `1px solid ${C.border}`,
              borderLeft: `2px solid ${C.red}`,
              padding: 36, overflow: 'hidden',
              alignSelf: 'stretch',
            }}>
              <Diamond size={340} color={C.red} style={{ position: 'absolute', right: -140, bottom: -140, opacity: 0.05, pointerEvents: 'none' }} />
              <Diamond size={140} color={C.orange} style={{ position: 'absolute', left: -50, top: -50, opacity: 0.04, pointerEvents: 'none' }} />

              {/* Top */}
              <div style={{ position: 'relative', zIndex: 2 }}>
                <Eyebrow>The Lobby</Eyebrow>
                <div style={{
                  fontFamily: F.display, fontSize: 52, letterSpacing: 1.5,
                  lineHeight: 0.95, color: C.text, margin: '14px 0 12px 0',
                }}>
                  WE NEVER<br />LEFT<span style={{ color: C.red }}>.</span>
                </div>
                <p style={{
                  fontFamily: F.body, fontSize: 14, lineHeight: 1.5,
                  color: C.textDim, fontWeight: 500, margin: 0, maxWidth: 340,
                }}>
                  Old engine. Real tick rate. A ladder that takes itself seriously. No CS2.
                </p>
              </div>

              {/* Stats */}
              <div style={{
                position: 'relative', zIndex: 2,
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, margin: '24px 0',
              }}>
                <PanelStat value="128" label="Tick rate" accent />
                <PanelStat value="FREE" label="Forever" />
                <PanelStat value="11" label="Ranks" />
                <PanelStat value="2" label="Modes" accent />
              </div>

              {/* Bottom */}
              <div style={{ position: 'relative', zIndex: 2 }}>
                <div style={{
                  display: 'flex', flexDirection: 'column', gap: 10,
                  paddingTop: 20, borderTop: `1px solid ${C.border}`,
                }}>
                  <StatusDot label="All servers online" />
                  <a
                    href={DISCORD_URL} target="_blank" rel="noopener noreferrer"
                    style={{
                      fontFamily: F.body, fontSize: 13, letterSpacing: 1.5,
                      color: C.textDim, textDecoration: 'none',
                      display: 'inline-flex', alignItems: 'center', gap: 10, width: 'fit-content',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = C.text)}
                    onMouseLeave={e => (e.currentTarget.style.color = C.textDim)}
                  >
                    <Diamond size={7} color={C.red} hollow={false} />
                    Beta access lives in the Discord →
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* FOOTER — fixed height, never shrinks */}
        <footer style={{
          borderTop: `1px solid ${C.border}`, flexShrink: 0,
          padding: isSmall ? '12px 20px' : '12px 32px',
          display: 'flex', flexDirection: isSmall ? 'column' : 'row',
          justifyContent: 'space-between', gap: 8,
          fontFamily: F.body, fontSize: 11, letterSpacing: 1,
          color: C.textVeryDim, fontWeight: 500,
        }}>
          <span>© 2026 Repeaked. The return to peak CS.</span>
          <span style={{ display: 'flex', gap: 18 }}>
            <a href="/terms" style={{ color: C.textVeryDim, textDecoration: 'none' }}>Terms</a>
            <a href="/privacy" style={{ color: C.textVeryDim, textDecoration: 'none' }}>Privacy</a>
            <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer" style={{ color: C.textVeryDim, textDecoration: 'none' }}>Discord</a>
          </span>
        </footer>
      </div>
    </div>
  );
}